import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { selectors } from "reducers";
import { fetchSynthpopCallLogs, exportSynthpopCallLogs } from "actions/synthpop-call-logs";
import { Waiting } from "components/ui";
import SearchableDropdown from "components/searchable-dropdown";
import AudioPlayer from "components/ui/audio-player";
import { get } from "utils/api";
import { useFetch } from "hooks";
import moment from "moment";
import MdRefresh from "react-icons/lib/md/refresh";
import FaFileExcelO from "react-icons/lib/fa/file-excel-o";
import FaExternalLink from "react-icons/lib/fa/external-link";
import FaFileTextO from "react-icons/lib/fa/file-text-o";
import FaHeadphones from "react-icons/lib/fa/headphones";
import FaPhone from "react-icons/lib/fa/phone";

const formatDate = date =>
  date ? moment(date).format("M/D/YY h:mma") : "";

// Group call logs by synthpopUUID and return the "best" record (secondary if exists, otherwise primary)
const groupCallLogsByUUID = (callLogs) => {
  const groups = {};

  callLogs.forEach(log => {
    const uuid = log.synthpopUUID;
    if (!uuid) {
      // No UUID, treat as standalone
      groups[`standalone_${log.id}`] = { primary: log, allAttempts: [log] };
      return;
    }

    if (!groups[uuid]) {
      groups[uuid] = { primary: null, secondary: null, allAttempts: [] };
    }

    groups[uuid].allAttempts.push(log);

    if (log.isSecondaryAttempt) {
      groups[uuid].secondary = log;
    } else {
      groups[uuid].primary = log;
    }
  });

  // Return array of { displayLog, allAttempts } - prefer secondary (better outcome)
  return Object.values(groups).map(group => ({
    displayLog: group.secondary || group.primary,
    allAttempts: group.allAttempts.sort((a, b) =>
      new Date(a.taskCreated) - new Date(b.taskCreated)
    )
  }));
};

const OUTCOME_OPTIONS = [
  { text: "All Outcomes", value: "" },
  { text: "Ordered Supplies", value: "ORDERED_SUPPLIES" },
  { text: "Declined Supplies", value: "DECLINED_SUPPLIES" },
  { text: "Requested Callback", value: "REQUESTED_CALL_BACK" },
  { text: "Left Callback Number", value: "LEFT_CALL_BACK_NUMBER" },
  { text: "Transferred", value: "TRANSFERRED" }
];

const NEEDS_ATTENTION_OPTIONS = [
  { text: "All", value: "" },
  { text: "Yes", value: "true" },
  { text: "No", value: "false" }
];

const PAGE_SIZE_OPTIONS = [50, 100, 150];

// Recording player component that fetches URI from recordingSid (preferred) or callSid (fallback)
const RecordingPlayer = ({ recordingSid, callSid }) => {
  const { response: uri, fetchData, isFetching } = useFetch({
    apiFn: sid => get(`callrecordings/${sid}/uri`),
    defaultValue: ""
  });

  const [showPlayer, setShowPlayer] = useState(false);

  // Use recordingSid if available, otherwise fall back to callSid
  const sidToUse = recordingSid || callSid;

  const handlePlay = () => {
    if (!uri && !isFetching) {
      fetchData(sidToUse);
    }
    setShowPlayer(true);
  };

  if (!sidToUse) return <span style={{ color: "#999" }}>-</span>;

  return (
    <div>
      {!showPlayer ? (
        <button
          onClick={handlePlay}
          title="Listen to recording"
          style={{ padding: "3px 8px", cursor: "pointer" }}
        >
          <FaHeadphones />
        </button>
      ) : isFetching ? (
        <span>Loading...</span>
      ) : uri ? (
        <AudioPlayer
          src={uri}
          preloadAudio="none"
          showAllFeatures={false}
          showTableFormatted={true}
        />
      ) : (
        <span style={{ color: "#999" }}>Not available</span>
      )}
    </div>
  );
};

// Format transcript with nice styling for each speaker
const formatTranscript = (transcriptText) => {
  if (!transcriptText) return null;

  // Split by newlines (handle both actual newlines and escaped \n)
  const lines = transcriptText.split(/\\n|\n/).filter(line => line.trim());

  // Track speakers dynamically - first speaker is assumed to be AI (since AI initiates the call)
  const speakerMap = {};
  let speakerCount = 0;

  const getSpeakerInfo = (speakerLabel) => {
    if (!speakerMap[speakerLabel]) {
      speakerCount++;
      // First speaker is AI (initiates call), second is Patient
      speakerMap[speakerLabel] = {
        displayName: speakerCount === 1 ? "AI" : speakerCount === 2 ? "Patient" : `Speaker ${speakerCount}`,
        isAI: speakerCount === 1,
        isPatient: speakerCount === 2
      };
    }
    return speakerMap[speakerLabel];
  };

  return lines.map((line, index) => {
    // Match any speaker pattern like "speaker_0:", "speaker_1:", "agent:", "customer:", etc.
    const speakerMatch = line.match(/^([a-zA-Z_]+\d*|speaker\d+|agent|customer|ai|patient):\s*/i);

    let speakerInfo = null;
    let displayLine = line;

    if (speakerMatch) {
      const speakerLabel = speakerMatch[1].toLowerCase();
      speakerInfo = getSpeakerInfo(speakerLabel);
      displayLine = `${speakerInfo.displayName}: ${line.substring(speakerMatch[0].length)}`;
    }

    const isAI = speakerInfo?.isAI;
    const isPatient = speakerInfo?.isPatient;

    return (
      <div
        key={index}
        style={{
          marginBottom: "12px",
          padding: "8px 12px",
          borderRadius: "8px",
          backgroundColor: isAI ? "#e3f2fd" : isPatient ? "#f5f5f5" : "transparent",
          borderLeft: isAI ? "3px solid #2196f3" : isPatient ? "3px solid #9e9e9e" : "none"
        }}
      >
        <span style={{ fontWeight: speakerInfo ? "600" : "normal" }}>
          {displayLine}
        </span>
      </div>
    );
  });
};

// Call attempts modal - shows all attempts for a patient call
const AttemptsModal = ({ attempts, onClose }) => {
  if (!attempts || attempts.length === 0) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "white",
          padding: "20px",
          borderRadius: "8px",
          maxWidth: "800px",
          maxHeight: "80vh",
          overflow: "auto",
          minWidth: "500px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.3)"
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px", borderBottom: "1px solid #eee", paddingBottom: "10px" }}>
          <h3 style={{ margin: 0 }}>Call Attempts ({attempts.length})</h3>
          <button
            onClick={onClose}
            style={{
              cursor: "pointer",
              border: "none",
              background: "#4281A6",
              color: "white",
              borderRadius: "4px",
              padding: "6px 14px",
              fontSize: "14px",
              fontWeight: "500"
            }}
          >
            Close
          </button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
          {attempts.map((attempt) => (
            <div
              key={attempt.id}
              style={{
                padding: "12px",
                border: "1px solid #ddd",
                borderRadius: "6px",
                backgroundColor: attempt.isSecondaryAttempt ? "#e8f5e9" : "#fafafa"
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                <span style={{ fontWeight: "600" }}>
                  {attempt.isSecondaryAttempt ? "Secondary Attempt" : "Primary Attempt"}
                </span>
                <span style={{ color: "#666", fontSize: "13px" }}>
                  {formatDate(attempt.taskCreated)}
                </span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", fontSize: "13px" }}>
                <div><strong>Answered By:</strong> {attempt.answeredBy || "-"}</div>
                <div><strong>Outcome:</strong> {attempt.outcome || "-"}</div>
                <div><strong>Disposition:</strong> {attempt.dispositionReason || "-"}</div>
                <div><strong>Call SID:</strong> <span style={{ fontFamily: "monospace", fontSize: "11px" }}>{attempt.callSid || "-"}</span></div>
              </div>
              <div style={{ marginTop: "8px" }}>
                <strong>Recording:</strong>{" "}
                <RecordingPlayer recordingSid={attempt.recordingSid} callSid={attempt.callSid} />
              </div>
              {attempt.errorStage && (
                <div style={{ marginTop: "8px", color: "#f44336" }}>
                  <strong>Error:</strong> {attempt.errorStage} - {attempt.errorMessage}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Transcript modal component
const TranscriptModal = ({ synthpopUUID, onClose }) => {
  const { response: data, fetchData, isFetching, error } = useFetch({
    apiFn: uuid => get(`synthpop/fetch-transcript/${uuid}`),
    defaultValue: null
  });

  useEffect(() => {
    if (synthpopUUID) {
      fetchData(synthpopUUID);
    }
  }, [synthpopUUID]);

  const transcriptText = data?.transcript || (typeof data === "string" ? data : null);

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "white",
          padding: "20px",
          borderRadius: "8px",
          maxWidth: "700px",
          maxHeight: "80vh",
          overflow: "auto",
          minWidth: "450px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.3)"
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px", borderBottom: "1px solid #eee", paddingBottom: "10px" }}>
          <h3 style={{ margin: 0 }}>Call Transcript</h3>
          <button
            onClick={onClose}
            style={{
              cursor: "pointer",
              border: "none",
              background: "#4281A6",
              color: "white",
              borderRadius: "4px",
              padding: "6px 14px",
              fontSize: "14px",
              fontWeight: "500"
            }}
          >
            Close
          </button>
        </div>
        {isFetching ? (
          <div style={{ textAlign: "center", padding: "20px" }}>Loading transcript...</div>
        ) : error ? (
          <div style={{ color: "red", padding: "20px" }}>
            Failed to load transcript.
          </div>
        ) : transcriptText ? (
          <div style={{ lineHeight: "1.5" }}>
            {formatTranscript(transcriptText)}
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "20px", color: "#666" }}>
            No transcript available.
          </div>
        )}
      </div>
    </div>
  );
};

const SynthpopCallLogsTable = ({ startDate, endDate, companyGuid }) => {
  const dispatch = useDispatch();
  const callLogs = useSelector(selectors.getSynthpopCallLogs);
  const totalCount = useSelector(selectors.getSynthpopCallLogsTotalCount);
  const currentPage = useSelector(selectors.getSynthpopCallLogsPage);
  const totalPages = useSelector(selectors.getSynthpopCallLogsTotalPages);
  const isFetching = useSelector(selectors.getIsFetchingSynthpopCallLogs);
  const allCompanies = useSelector(selectors.getFormOptionsCompany);

  // Filter to only AI calling companies
  const aiCallingCompanies = (allCompanies || []).filter(c => c.agent_calling);

  // Group call logs by UUID - show secondary attempt if exists (better outcome)
  const groupedCallLogs = useMemo(() => groupCallLogsByUUID(callLogs), [callLogs]);

  // Modal states
  const [transcriptUUID, setTranscriptUUID] = useState(null);
  const [attemptsModalData, setAttemptsModalData] = useState(null);

  // Filters state
  const [filters, setFilters] = useState({
    outcome: "",
    needsAttention: "",
    companyGuid: companyGuid || "",
    page: 1,
    pageSize: 50
  });

  const buildParams = useCallback(() => {
    const isoStart = moment(startDate, "MM/DD/YYYY").format("YYYY-MM-DD");
    const isoEnd = moment(endDate, "MM/DD/YYYY").format("YYYY-MM-DD");

    return {
      startDate: isoStart,
      endDate: isoEnd,
      companyGuid: filters.companyGuid || null,
      outcome: filters.outcome || null,
      needsAttention: filters.needsAttention === "" ? null : filters.needsAttention === "true",
      page: filters.page,
      pageSize: filters.pageSize
    };
  }, [startDate, endDate, filters]);

  const fetchData = useCallback(() => {
    dispatch(fetchSynthpopCallLogs(buildParams()));
  }, [dispatch, buildParams]);

  useEffect(() => {
    fetchData();
  }, [startDate, endDate, filters]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newSize) => {
    setFilters(prev => ({ ...prev, pageSize: parseInt(newSize, 10), page: 1 }));
  };

  const handleExport = () => {
    dispatch(exportSynthpopCallLogs(buildParams()));
  };

  const companyOptions = [
    { text: "All Agent Calling Companies", value: "" },
    ...aiCallingCompanies.map(c => ({ text: c.text, value: c.value }))
  ];


  return (
    <div style={{
      padding: "10px",
      display: "flex",
      flexDirection: "column",
      height: "calc(100vh - 200px)"
    }}>
      {/* Transcript Modal */}
      {transcriptUUID && (
        <TranscriptModal
          synthpopUUID={transcriptUUID}
          onClose={() => setTranscriptUUID(null)}
        />
      )}

      {/* Attempts Modal */}
      {attemptsModalData && (
        <AttemptsModal
          attempts={attemptsModalData}
          onClose={() => setAttemptsModalData(null)}
        />
      )}

      {/* Header with filters */}
      <div style={{ display: "flex", alignItems: "center", gap: "15px", marginBottom: "15px", flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <label>Company:</label>
          <SearchableDropdown
            options={companyOptions}
            selected={filters.companyGuid}
            onSelect={val => handleFilterChange("companyGuid", val)}
            style={{ minWidth: "200px" }}
          />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <label>Agent Outcome:</label>
          <select
            value={filters.outcome}
            onChange={e => handleFilterChange("outcome", e.target.value)}
          >
            {OUTCOME_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.text}</option>
            ))}
          </select>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <label>Needs Attention:</label>
          <select
            value={filters.needsAttention}
            onChange={e => handleFilterChange("needsAttention", e.target.value)}
          >
            {NEEDS_ATTENTION_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.text}</option>
            ))}
          </select>
        </div>
        <button onClick={fetchData} title="Refresh" style={{ padding: "5px 10px" }}>
          <MdRefresh />
        </button>
        <button onClick={handleExport} title="Export to CSV" style={{ padding: "5px 10px" }}>
          <FaFileExcelO /> Export
        </button>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "5px" }}>
          <label>Per Page:</label>
          <select value={filters.pageSize} onChange={e => handlePageSizeChange(e.target.value)}>
            {PAGE_SIZE_OPTIONS.map(size => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      {isFetching ? (
        <Waiting reason="Loading call logs..." />
      ) : (
        <div style={{ flex: 1, overflowY: "auto", overflowX: "auto" }}>
          <table className="table" style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap", margin: 0 }}>
            <thead style={{ position: "sticky", top: 0, backgroundColor: "#fff", zIndex: 1 }}>
              <tr>
                <th style={{ backgroundColor: "#fff" }}>Date</th>
                <th style={{ backgroundColor: "#fff" }}>Call SID</th>
                <th style={{ backgroundColor: "#fff" }}>Patient</th>
                <th style={{ backgroundColor: "#fff" }}>Company</th>
                <th style={{ backgroundColor: "#fff" }}>Agent Outcome</th>
                <th style={{ backgroundColor: "#fff" }}>Disposition</th>
                <th style={{ backgroundColor: "#fff" }}>Tasks</th>
                <th style={{ backgroundColor: "#fff" }}>Order</th>
                <th style={{ backgroundColor: "#fff" }}>Recording</th>
                <th style={{ backgroundColor: "#fff" }}>Actions</th>
                <th style={{ backgroundColor: "#fff" }}>Errors</th>
              </tr>
            </thead>
            <tbody>
              {groupedCallLogs.length === 0 ? (
                <tr>
                  <td colSpan="11" style={{ textAlign: "center", padding: "20px" }}>
                    No call logs found for the selected filters.
                  </td>
                </tr>
              ) : (
                groupedCallLogs.map(({ displayLog: log, allAttempts }) => (
                  <tr key={log.id}>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                        {formatDate(log.taskCreated)}
                        {allAttempts.length > 1 && (
                          <button
                            onClick={() => setAttemptsModalData(allAttempts)}
                            title="View all call attempts"
                            style={{
                              backgroundColor: "#607d8b",
                              color: "white",
                              border: "none",
                              padding: "2px 6px",
                              borderRadius: "3px",
                              fontSize: "10px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: "3px"
                            }}
                          >
                            <FaPhone style={{ fontSize: "8px" }} />
                            {allAttempts.length}
                          </button>
                        )}
                      </div>
                    </td>
                    <td>
                      <span style={{ fontSize: "11px", fontFamily: "monospace" }}>
                        {log.callSid || "-"}
                      </span>
                    </td>
                    <td>
                      {log.patientGuid ? (
                        <Link to={`/patients/${log.patientGuid}`}>{log.patientName}</Link>
                      ) : (
                        log.patientName
                      )}
                    </td>
                    <td>
                      {log.companyGuid ? (
                        <Link to={`/companies/${log.companyGuid}/profile`}>{log.companyName}</Link>
                      ) : (
                        log.companyName
                      )}
                    </td>
                    <td>{log.outcome || "-"}</td>
                    <td>{log.dispositionReason || "-"}</td>
                    <td>
                      {/* Tasks column */}
                      <div style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
                        {log.tasksCreated && log.tasksCreated.length > 0 ? (
                          log.tasksCreated.map((taskType, idx) => (
                            <span
                              key={idx}
                              style={{
                                backgroundColor: "#9c27b0",
                                color: "white",
                                padding: "2px 6px",
                                borderRadius: "3px",
                                fontSize: "11px"
                              }}
                            >
                              {taskType}
                            </span>
                          ))
                        ) : (
                          <span style={{ color: "#999" }}>-</span>
                        )}
                      </div>
                    </td>
                    <td>
                      {log.orderGuid ? (
                        <Link to={`/orders/${log.orderGuid}`}>View Order</Link>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>
                      <RecordingPlayer recordingSid={log.recordingSid} callSid={log.callSid} />
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: "5px" }}>
                        {log.synthpopTaskURL && (
                          <a
                            href={log.synthpopTaskURL}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="View in Synthpop"
                            style={{ padding: "3px 8px" }}
                          >
                            <FaExternalLink />
                          </a>
                        )}
                        {log.synthpopUUID && (
                          <button
                            onClick={() => setTranscriptUUID(log.synthpopUUID)}
                            title="View Transcript"
                            style={{ padding: "3px 8px", cursor: "pointer" }}
                          >
                            <FaFileTextO />
                          </button>
                        )}
                      </div>
                    </td>
                    <td>
                      {/* Errors column */}
                      <div style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
                        {log.needsAttention && (
                          <span
                            style={{
                              backgroundColor: "#ff9800",
                              color: "white",
                              padding: "2px 6px",
                              borderRadius: "3px",
                              fontSize: "11px"
                            }}
                            title="Needs Attention"
                          >
                            Attention
                          </span>
                        )}
                        {log.errorStage && (
                          <span
                            style={{
                              backgroundColor: "#f44336",
                              color: "white",
                              padding: "2px 6px",
                              borderRadius: "3px",
                              fontSize: "11px",
                              cursor: "help"
                            }}
                            title={log.errorMessage || log.errorStage}
                          >
                            {log.errorStage}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer with pagination and count */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background: "#eee",
        border: "1px solid #ddd",
        padding: "10px 15px",
        flexShrink: 0
      }}>
        {/* Pagination */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1, justifyContent: "center" }}>
          {totalPages > 1 ? (
            <>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                style={{ padding: "4px 12px", cursor: currentPage === 1 ? "not-allowed" : "pointer" }}
              >
                Previous
              </button>
              <span>
                Page{" "}
                <select
                  value={currentPage}
                  onChange={(e) => handlePageChange(Number(e.target.value))}
                  style={{ width: "50px", margin: "0 5px" }}
                >
                  {Array.from({ length: totalPages }, (_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1}</option>
                  ))}
                </select>
                of {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
                style={{ padding: "4px 12px", cursor: currentPage >= totalPages ? "not-allowed" : "pointer" }}
              >
                Next
              </button>
            </>
          ) : (
            <span>&nbsp;</span>
          )}
        </div>
        {/* Total count */}
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <span style={{ color: "#4281a6" }}>Total:</span>
          <span style={{ fontWeight: "bold" }}>{totalCount}</span>
        </div>
      </div>
    </div>
  );
};

export default SynthpopCallLogsTable;
